import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { isAuthFlowUrl, isReturnableUrl, resolveAuthRedirect } from '../Helper/auth-redirect';
import { UserStateService } from './user-state.service';
import { WebStorageService } from './web-storage.service';

/** sessionStorage key for the page the user left to sign in / sign up. */
export const AUTH_RETURN_URL_KEY = 'authReturnUrl';

/** sessionStorage key for the link this tab was opened on while logged out (a shared link). */
export const AUTH_OPENED_LINK_KEY = 'authOpenedLink';

/**
 * localStorage key that carries the destination across to the tab the verification email
 * opens. sessionStorage is per tab, and the email link always lands in a new one.
 */
export const AUTH_RETURN_HANDOFF_KEY = 'authReturnHandoff';

/** How long the email-verification hand-off stays usable. */
export const AUTH_RETURN_HANDOFF_TTL_MS = 60 * 60 * 1000;

/**
 * Returns a user to the page they were on before they went to sign in or sign up.
 *
 * The page is captured centrally, from router history, instead of at each "Log in" button:
 * every entry into the auth flow — header and page login links, the 401 interceptor,
 * `router.navigate(['/sign-in'])` in feature code, route guards — passes through the router,
 * so all of them are covered without each one having to remember to save the URL first.
 *
 * A shared link is captured too: the URL a logged-out user opened the tab on wins over the
 * page they were on, because many pages bounce a logged-out visitor elsewhere on load
 * (`/loan-request` → `/borrow`) and the page they finally sign in from is not the one they
 * were sent. It is dropped as soon as the user moves on to another page themselves.
 *
 * Kept in sessionStorage, so it is per tab and gone when the tab closes.
 */
@Injectable({ providedIn: 'root' })
export class AuthReturnUrlService {
    private started = false;

    /** Set by the first click, key press or back/forward after the app loads. */
    private userActed = false;

    constructor(
        private router: Router,
        private webStorageService: WebStorageService,
        private userState: UserStateService
    ) { }

    /**
     * Starts watching navigations. Called once from an APP_INITIALIZER so the very first
     * navigation is seen — that is what tells a direct visit to /sign-in apart from a click.
     */
    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        // A page bouncing a logged-out visitor happens on load, before any input; a later
        // move to another page is the user's own choice. Capture phase, so a handler that
        // stops propagation cannot hide the input.
        const markUserAction = () => { this.userActed = true; };
        document.addEventListener('pointerdown', markUserAction, true);
        document.addEventListener('keydown', markUserAction, true);

        let previousUrl: string | null = null;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                if (event.navigationTrigger === 'popstate') {
                    this.userActed = true;
                }
                return;
            }
            if (!(event instanceof NavigationEnd)) {
                return;
            }

            const url = event.urlAfterRedirects;
            if (previousUrl === null) {
                // `event.url`, not `urlAfterRedirects`: the link as opened, before any guard
                // redirect, so `/` (redirected to the dashboard) never counts as a shared link.
                this.onFirstPage(event.url);
            } else if (this.userActed && !isAuthFlowUrl(url) && !isAuthFlowUrl(previousUrl)) {
                this.webStorageService.removeItem(AUTH_OPENED_LINK_KEY);
            }

            // Only the step INTO the auth flow decides the destination. Moving between auth
            // pages (sign-in → sign-up → verify-email) keeps what was captured on the way in.
            if (isAuthFlowUrl(url) && !isAuthFlowUrl(previousUrl)) {
                if (previousUrl === null) {
                    this.onAuthFlowPageLoad();
                } else if (isReturnableUrl(previousUrl)) {
                    this.webStorageService.setItem(AUTH_RETURN_URL_KEY, previousUrl);
                } else {
                    this.webStorageService.removeItem(AUTH_RETURN_URL_KEY);
                }
            }

            if (url.split(/[?#]/)[0] === '/verify-email') {
                this.handOffForEmailVerification(url);
            }
            previousUrl = url;
        });
    }

    /**
     * The first page of this app load. A logged-out user opening a page directly — a shared
     * link — is remembered so they come back to it after signing in.
     *
     * - Signed in already: nothing to come back to.
     * - Opened fresh (new tab, typed URL, external link): this link replaces any older one;
     *   a direct visit to `/`, `/sign-in` or another non-returnable page clears it.
     * - Refresh, or a full-document navigation from one of our own pages (the service-worker
     *   update): keep the link this tab already holds; only fill it in when there is none.
     */
    private onFirstPage(requestedUrl: string): void {
        if (this.userState.user) {
            this.webStorageService.removeItem(AUTH_OPENED_LINK_KEY);
            return;
        }

        const freshEntry = this.navigationType() !== 'reload' && !this.sameOriginReferrerPath();
        if (isReturnableUrl(requestedUrl)) {
            if (freshEntry || !this.webStorageService.getItem(AUTH_OPENED_LINK_KEY)) {
                this.webStorageService.setItem(AUTH_OPENED_LINK_KEY, requestedUrl);
            }
        } else if (freshEntry) {
            this.webStorageService.removeItem(AUTH_OPENED_LINK_KEY);
        }
    }

    /**
     * The app booted straight onto an auth page, so there is no in-app previous page.
     *
     * - Refresh (F5) of /sign-in: keep what was captured before the refresh.
     * - Full-document navigation from one of our own pages — the service-worker update applies
     *   a pending deploy by turning the next click (often "Log in") into `location.assign`:
     *   the page is still there as a same-origin referrer, so return to it.
     * - Typed URL, bookmark, external link: no destination, and any value left from an
     *   earlier, abandoned attempt is dropped.
     */
    private onAuthFlowPageLoad(): void {
        if (this.navigationType() === 'reload') {
            return;
        }
        const fromPage = this.sameOriginReferrerPath();
        if (isReturnableUrl(fromPage)) {
            this.webStorageService.setItem(AUTH_RETURN_URL_KEY, fromPage);
        } else {
            this.webStorageService.removeItem(AUTH_RETURN_URL_KEY);
        }
    }

    /**
     * The user is waiting on the verification email. Its link opens a new tab, which cannot
     * read this tab's sessionStorage, so copy the destination to localStorage for a short
     * while; the new tab picks it up when it signs in or closes the "Account verified" modal.
     */
    private handOffForEmailVerification(url: string): void {
        const destination = this.validOrNull(resolveAuthRedirect(this.router.parseUrl(url).queryParams?.['redirect']))
            ?? this.validOrNull(this.webStorageService.getItem(AUTH_OPENED_LINK_KEY))
            ?? this.validOrNull(this.webStorageService.getItem(AUTH_RETURN_URL_KEY));
        if (destination) {
            this.webStorageService.setLocalStorage(AUTH_RETURN_HANDOFF_KEY, {
                url: destination,
                expiresAt: Date.now() + AUTH_RETURN_HANDOFF_TTL_MS
            });
        } else {
            this.webStorageService.removeLocalStorageItem(AUTH_RETURN_HANDOFF_KEY);
        }
    }

    private navigationType(): string | undefined {
        try {
            const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
            return entry?.type;
        } catch {
            return undefined;
        }
    }

    /** Path + query of `document.referrer` when it is this origin, else null. */
    private sameOriginReferrerPath(): string | null {
        try {
            if (!document.referrer) {
                return null;
            }
            const referrer = new URL(document.referrer);
            return referrer.origin === window.location.origin ? referrer.pathname + referrer.search : null;
        } catch {
            return null;
        }
    }

    /**
     * Where to send a user who has just authenticated on an auth page, and forgets it so
     * it is used once.
     *
     * In order: a guard's `?redirect=` token on the current URL (the user was turned away
     * from that exact page), the shared link this tab was opened on, the page captured on
     * the way into the auth flow, then the email-verification hand-off from another tab.
     *
     * @returns the destination, or null when there is none and the caller's default applies.
     */
    consume(): string | null {
        const requested = resolveAuthRedirect(
            this.router.parseUrl(this.router.url).queryParams?.['redirect']
        );
        const openedLink = this.validOrNull(this.webStorageService.getItem(AUTH_OPENED_LINK_KEY));
        const stored = this.validOrNull(this.webStorageService.getItem(AUTH_RETURN_URL_KEY));
        const handedOff = this.readHandOff();
        this.forget();
        return requested ?? openedLink ?? stored ?? handedOff;
    }

    /**
     * Like `consume()`, for sign-in that happens on a normal page (the inline login card,
     * the header wallet connect, the "Account verified" modal) rather than on an auth page:
     * only the shared link or the email-verification hand-off apply. The page captured on
     * the way into the auth flow is ignored — the user is not coming from there.
     *
     * @returns the destination, or null when the user should stay where they are.
     */
    consumeOpenedLink(): string | null {
        const openedLink = this.validOrNull(this.webStorageService.getItem(AUTH_OPENED_LINK_KEY));
        const handedOff = this.readHandOff();
        this.forget();
        return openedLink ?? handedOff;
    }

    /** Forgets every remembered destination, so a later sign-in uses its default page. */
    forget(): void {
        this.webStorageService.removeItem(AUTH_OPENED_LINK_KEY);
        this.webStorageService.removeItem(AUTH_RETURN_URL_KEY);
        this.webStorageService.removeLocalStorageItem(AUTH_RETURN_HANDOFF_KEY);
    }

    private readHandOff(): string | null {
        try {
            const raw = this.webStorageService.getLocalStorage(AUTH_RETURN_HANDOFF_KEY);
            const handOff = raw ? JSON.parse(raw) : null;
            return handOff && Number(handOff.expiresAt) > Date.now() ? this.validOrNull(handOff.url) : null;
        } catch {
            return null;
        }
    }

    private validOrNull(url: string | null | undefined): string | null {
        return isReturnableUrl(url) ? url : null;
    }

    /**
     * Re-enters `url` with a fresh component instance even when it is the current URL.
     *
     * For the inline login card: the page it sits in read the logged-out state once at init,
     * and a plain same-URL navigation reuses that component, so the card would stay on screen.
     */
    reenter(url: string): Promise<boolean> {
        const strategy = this.router.routeReuseStrategy;
        const shouldReuseRoute = strategy.shouldReuseRoute;
        strategy.shouldReuseRoute = () => false;
        return this.router
            .navigateByUrl(url, { onSameUrlNavigation: 'reload' })
            .finally(() => {
                strategy.shouldReuseRoute = shouldReuseRoute;
            });
    }
}
